import React, {useEffect, useMemo, useState} from 'react';
import {HeaderTabs} from "../../Layout/HeaderTabs";
import {
  Container,
  Title,
  Text,
  Alert,
  Button,
  Group,
  Stack,
  Card,
  Image,
  Badge,
  Box,
  Loader,
  TextInput,
  UnstyledButton,
  ScrollArea,
  ActionIcon,
  Modal,
  Switch
} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronRight,
  IconFolder,
  IconFolderOpen,
  IconInfoCircle,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconUpload
} from "@tabler/icons-react";
import {useNavigate, useLocation} from "react-router-dom";
import useGalleries, {Gallery} from "../Galeries/hooks/useGalleries";
import useLocalStorage from "use-local-storage";
import useGalleriesYears from "../Galeries/hooks/useGalleriesYears";
import useCreateGallery from "../Galeries/hooks/useCreateGallery";
import GalleryForm from "../Galeries/Components/GalleryForm";
import processAxiosError from "../../../utils/processAxiosError";
import ErrorBoundary from "../../Shared/ErrorBoundary";

interface ShareTargetProps {
}

interface TreeNode {
  gallery: Gallery;
  children: TreeNode[];
}

const normalize = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const ShareTarget = (props: ShareTargetProps) => {
  const [selectedGallery, setSelectedGallery] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useLocalStorage<number | null>("galleries-year", null);
  const [sharedImages, setSharedImages] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [createOpened, {open: openCreate, close: closeCreate}] = useDisclosure(false);
  const [createAsChild, setCreateAsChild] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const galleries = useGalleries(selectedYear);
  const years = useGalleriesYears();
  const createGallery = useCreateGallery();

  useEffect(() => {
    if (years?.data?.length && !selectedYear) {
      setSelectedYear(years?.data[0]?.year);
    }
  }, [years, selectedYear, setSelectedYear]);

  useEffect(() => {
    const handleSharedFiles = async () => {
      const searchParams = new URLSearchParams(location.search);
      const shareId = searchParams.get('shareId');

      if (shareId) {
        try {
          const cache = await caches.open('share-target-cache');
          const metadataResponse = await cache.match(`/shared-metadata-${shareId}`);

          if (metadataResponse) {
            const fileMetadata = await metadataResponse.json();
            const files: File[] = [];

            for (const metadata of fileMetadata) {
              const fileResponse = await cache.match(metadata.cacheKey);
              if (fileResponse) {
                const arrayBuffer = await fileResponse.arrayBuffer();
                const file = new File([arrayBuffer], metadata.name, {
                  type: metadata.type,
                  lastModified: metadata.lastModified
                });
                files.push(file);
              }
            }

            setSharedImages(files);

            setTimeout(async () => {
              try {
                for (const metadata of fileMetadata) {
                  await cache.delete(metadata.cacheKey);
                }
                await cache.delete(`/shared-metadata-${shareId}`);
              } catch (error) {
                console.error('Error cleaning up cache:', error);
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error retrieving shared files:', error);
        }
      } else if (location.state?.formData) {
        const formData = location.state.formData as FormData;
        const files = formData.getAll('images') as File[];
        setSharedImages(files);
      } else {
        console.log('Share target accessed, but no shared files found');
      }
    };

    handleSharedFiles();
  }, [location]);

  const galleriesById = useMemo(() => {
    const map = new Map<number, Gallery>();
    for (const g of galleries.data ?? []) map.set(g.id, g);
    return map;
  }, [galleries.data]);

  // Build a tree of galleries relevant to the selected year.
  // The year API returns sub-galleries from this year plus their parents
  // (even if those parents are from older years), so we include the parent
  // chain even when the parent's date is outside the selected year.
  const tree = useMemo<TreeNode[]>(() => {
    if (!galleries.data || !selectedYear) return [];

    const inYear = (g: Gallery) => new Date(g.date).getFullYear() === selectedYear;
    const visible = new Set<number>();

    for (const g of galleries.data) {
      if (!inYear(g)) continue;
      visible.add(g.id);
      // Walk up the parent chain so parents render as folder headers.
      let cursor: Gallery | undefined = g;
      while (cursor?.upper_gallery) {
        const parent = galleriesById.get(cursor.upper_gallery);
        if (!parent || visible.has(parent.id)) break;
        visible.add(parent.id);
        cursor = parent;
      }
    }

    const nodes = new Map<number, TreeNode>();
    visible.forEach(id => {
      const g = galleriesById.get(id);
      if (g) nodes.set(id, {gallery: g, children: []});
    });

    const roots: TreeNode[] = [];
    nodes.forEach(node => {
      const parentId = node.gallery.upper_gallery;
      const parent = parentId ? nodes.get(parentId) : undefined;
      if (parent) parent.children.push(node);
      else roots.push(node);
    });

    const sortNodes = (list: TreeNode[]) => {
      list.sort((a, b) =>
        new Date(b.gallery.date).getTime() - new Date(a.gallery.date).getTime()
      );
      for (const n of list) sortNodes(n.children);
    };
    sortNodes(roots);

    return roots;
  }, [galleries.data, galleriesById, selectedYear]);

  // Filter the tree against the search term, keeping parents that contain matches.
  const filteredTree = useMemo<TreeNode[]>(() => {
    if (!search.trim()) return tree;
    const q = normalize(search.trim());
    const matches = (node: TreeNode): TreeNode | null => {
      const kept = node.children.map(matches).filter(Boolean) as TreeNode[];
      const selfMatch = normalize(node.gallery.title).includes(q) ||
        node.gallery.date.includes(q);
      if (selfMatch || kept.length) {
        return {gallery: node.gallery, children: kept};
      }
      return null;
    };
    return tree.map(matches).filter(Boolean) as TreeNode[];
  }, [tree, search]);

  const hasAnyGallery = tree.length > 0;
  const hasFilteredResults = filteredTree.length > 0;

  // Auto-expand parents when searching so matches are visible.
  useEffect(() => {
    if (!search.trim()) return;
    const ids = new Set<number>();
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        if (n.children.length) {
          ids.add(n.gallery.id);
          walk(n.children);
        }
      }
    };
    walk(filteredTree);
    setExpanded(ids);
  }, [search, filteredTree]);

  // When the selected gallery changes, make sure its parent chain is expanded.
  useEffect(() => {
    if (!selectedGallery) return;
    setExpanded(prev => {
      const next = new Set(prev);
      let cursor = galleriesById.get(selectedGallery);
      while (cursor?.upper_gallery) {
        next.add(cursor.upper_gallery);
        cursor = galleriesById.get(cursor.upper_gallery);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [selectedGallery, galleriesById]);

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Auto-select the latest gallery from the year (deepest available leaf of the newest root).
  useEffect(() => {
    if (selectedGallery || !tree.length) return;
    const pickLatest = (nodes: TreeNode[]): Gallery => {
      const sorted = [...nodes].sort((a, b) =>
        new Date(b.gallery.date).getTime() - new Date(a.gallery.date).getTime()
      );
      const first = sorted[0];
      return first.gallery;
    };
    setSelectedGallery(pickLatest(tree).id);
  }, [tree, selectedGallery]);

  const selectedGalleryData = selectedGallery ? galleriesById.get(selectedGallery) : null;
  const selectedParent = selectedGalleryData?.upper_gallery
    ? galleriesById.get(selectedGalleryData.upper_gallery)
    : null;

  const handleUpload = () => {
    if (selectedGallery) {
      setIsProcessing(true);
      navigate(`/galeries/${selectedGallery}/upload`, {
        state: {sharedImages}
      });
    }
  };

  const handleOpenCreate = () => {
    setCreateAsChild(false);
    createGallery.reset();
    openCreate();
  };

  const handleCreateSubmit = (values: any) => {
    createGallery.mutateAsync(values).then(res => {
      const newGallery = res.data as Gallery;
      const newYear = new Date(newGallery.date).getFullYear();
      if (newYear !== selectedYear) setSelectedYear(newYear);
      setSelectedGallery(newGallery.id);
      setSearch("");
      closeCreate();
    });
  };

  const createError = processAxiosError(createGallery?.error);
  const createParentId = createAsChild && selectedGallery ? selectedGallery : null;

  const renderNode = (node: TreeNode, depth: number) => {
    const {gallery} = node;
    const hasChildren = node.children.length > 0;
    const isOpen = expanded.has(gallery.id);
    const isSelected = selectedGallery === gallery.id;

    return (
      <Box key={gallery.id}>
        <UnstyledButton
          onClick={() => setSelectedGallery(gallery.id)}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 10px',
            paddingLeft: 10 + depth * 18,
            borderRadius: 6,
            backgroundColor: isSelected ? 'var(--mantine-color-green-1)' : undefined,
            border: isSelected
              ? '1px solid var(--mantine-color-green-6)'
              : '1px solid transparent',
          }}
        >
          <Group gap={6} wrap="nowrap">
            {hasChildren ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(gallery.id);
                }}
                aria-label={isOpen ? "Sbalit" : "Rozbalit"}
              >
                {isOpen ? <IconChevronDown size={16}/> : <IconChevronRight size={16}/>}
              </ActionIcon>
            ) : (
              <Box w={28}/>
            )}
            {hasChildren ? (
              isOpen
                ? <IconFolderOpen size={18} color="var(--mantine-color-yellow-7)"/>
                : <IconFolder size={18} color="var(--mantine-color-yellow-7)"/>
            ) : (
              <IconPhoto size={18} color="var(--mantine-color-gray-6)"/>
            )}
            <Box style={{flex: 1, minWidth: 0}}>
              <Text fw={isSelected ? 600 : 500} size="sm" truncate>
                {gallery.title}
              </Text>
              <Text size="xs" c="dimmed">{gallery.date}</Text>
            </Box>
            {isSelected && (
              <Badge color="green" variant="filled" size="sm">Vybráno</Badge>
            )}
          </Group>
        </UnstyledButton>

        {hasChildren && isOpen && (
          <Stack gap={2} mt={2}>
            {node.children.map(child => renderNode(child, depth + 1))}
          </Stack>
        )}
      </Box>
    );
  };

  if (galleries.isLoading || years.isLoading) {
    return (
      <>
        <HeaderTabs/>
        <Container size="md" mb={20}>
          <Group justify="center" mt="xl">
            <Loader size="lg"/>
            <Text>Načítám galerie...</Text>
          </Group>
        </Container>
      </>
    );
  }

  return (
    <>
      <HeaderTabs/>
      <Container size="md" mb={20}>
        <Stack gap="md">
          <Box>
            <Title order={1} size="1.5rem" fw="bold">
              <IconPhoto style={{marginRight: 8}}/>
              Sdílené obrázky
            </Title>
            <Text c="dimmed" size="sm">
              Vyberte galerii, kam chcete nahrát sdílené obrázky
            </Text>
          </Box>

          {sharedImages.length > 0 && (
            <Card withBorder padding="md">
              <Text fw={500} mb="sm">
                Sdílené obrázky ({sharedImages.length})
              </Text>
              <Group gap="sm">
                {sharedImages.slice(0, 5).map((file, index) => (
                  <Box key={index} style={{position: 'relative'}}>
                    <Image
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      width={80}
                      height={80}
                      fit="cover"
                      radius="sm"
                    />
                    <Badge
                      size="xs"
                      variant="filled"
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        fontSize: '10px'
                      }}
                    >
                      {Math.round(file.size / 1024)}KB
                    </Badge>
                  </Box>
                ))}
                {sharedImages.length > 5 && (
                  <Box
                    style={{
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'var(--mantine-color-gray-1)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      border: '1px dashed var(--mantine-color-gray-4)'
                    }}
                  >
                    <Text size="sm" c="dimmed">
                      +{sharedImages.length - 5}
                    </Text>
                  </Box>
                )}
              </Group>
            </Card>
          )}

          {sharedImages.length === 0 && (
            <Alert variant="light" color="blue" title="Žádné obrázky" icon={<IconInfoCircle/>}>
              Nebyly nalezeny žádné sdílené obrázky. Zkuste použít funkci sdílení z galerie vašeho zařízení.
            </Alert>
          )}

          {years?.data && years.data.length > 0 && (
            <Box>
              <Text fw={500} mb="xs">Vyberte rok</Text>
              <Group gap="xs">
                {years.data.map(year => (
                  <Button
                    key={year.year}
                    variant={year.year === selectedYear ? "filled" : "outline"}
                    size="sm"
                    color="green"
                    onClick={() => {
                      setSelectedYear(year.year);
                      setSelectedGallery(null);
                      setSearch("");
                    }}
                  >
                    {year.year}
                  </Button>
                ))}
              </Group>
            </Box>
          )}

          <Box>
            <Group justify="space-between" mb="xs" wrap="nowrap">
              <Text fw={500}>Vyberte galerii</Text>
              <Button
                size="xs"
                variant="light"
                color="green"
                leftSection={<IconPlus size={14}/>}
                onClick={handleOpenCreate}
              >
                Nová galerie
              </Button>
            </Group>

            {hasAnyGallery && selectedGalleryData && (
              <Text size="xs" c="dimmed" mb="xs" truncate>
                {selectedParent
                  ? `${selectedParent.title} / ${selectedGalleryData.title}`
                  : selectedGalleryData.title}
              </Text>
            )}

            {hasAnyGallery && (
              <>
                <TextInput
                  placeholder="Hledat galerii..."
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  leftSection={<IconSearch size={16}/>}
                  mb="xs"
                />
                <Card withBorder padding={6}>
                  <ScrollArea.Autosize mah={360}>
                    {hasFilteredResults ? (
                      <Stack gap={2}>
                        {filteredTree.map(node => renderNode(node, 0))}
                      </Stack>
                    ) : (
                      <Text c="dimmed" size="sm" ta="center" py="md">
                        Žádná galerie neodpovídá hledání.
                      </Text>
                    )}
                  </ScrollArea.Autosize>
                </Card>
              </>
            )}

            {!hasAnyGallery && selectedYear && (
              <Alert variant="light" color="yellow" title="Žádné galerie" icon={<IconInfoCircle/>}>
                Pro rok {selectedYear} nejsou k dispozici žádné galerie. Vytvořte novou galerii nebo vyberte jiný rok.
              </Alert>
            )}
          </Box>

          {galleries.isError && (
            <Alert variant="light" color="red" title="Chyba" icon={<IconInfoCircle/>}>
              Nepodařilo se načíst galerie. Zkuste stránku obnovit.
            </Alert>
          )}

          <Group justify="space-between" mt="xl">
            <Button
              variant="outline"
              color="gray"
              onClick={() => navigate('/galeries')}
            >
              Zpět na galerie
            </Button>

            <Button
              leftSection={<IconUpload size={16}/>}
              onClick={handleUpload}
              disabled={!selectedGallery || isProcessing}
              loading={isProcessing}
              color="green"
            >
              Nahrát do galerie
            </Button>
          </Group>
        </Stack>

        <ErrorBoundary>
          <Modal
            opened={createOpened}
            onClose={closeCreate}
            title="Vytvořit novou galerii"
            closeOnClickOutside={false}
            size="xl"
          >
            {selectedGalleryData && (
              <Switch
                mb="md"
                label={`Vytvořit jako podgalerii: ${selectedGalleryData.title}`}
                checked={createAsChild}
                onChange={(e) => setCreateAsChild(e.currentTarget.checked)}
              />
            )}

            {createError && (
              <Alert
                variant="light"
                color="red"
                title={createError?.title}
                icon={<IconInfoCircle/>}
                mb="md"
              >
                {createError?.text}
              </Alert>
            )}

            {createOpened && (
              <GalleryForm
                loading={createGallery.isPending}
                handleSubmit={handleCreateSubmit}
                upperId={createParentId}
              />
            )}
          </Modal>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default ShareTarget;
